import * as React from "react";
import { useTheme } from "react-jss";
import { useSpring, interpolate, animated } from "react-spring";
import { useDrag } from "react-use-gesture";
import { uniq, clamp } from "../../utils/lodash";
import useStyles from "./CardSortControl.styles";
import { useLanguage } from "../../hooks/useLanguage";
import { ControlProps } from "./BaseControl";
import { translate } from "../../models/utilities/translationUtilities";
import { useElementDimensions } from "../../hooks/useElementDimensions";
import useRecall from "../../hooks/useRecall";
import { VariableModel } from "../../models/VariableModel";
import { usePickMentions, useSetVariableMentions } from "../../hooks/useMentions";
import { usePrevious } from "../../hooks/usePrevious";
import { ChoiceModel } from "../../models/ChoiceModel";
import { useValidation, useDiscreteVariadicValidator } from "../../hooks/useValidation";
import { getSettingString } from "../../models/utilities/settingUtilities";
import { getChoice } from "../../models/utilities/variableUtilities";

const CARD_SIZE_RATIO = 3 / 5;
const PADDING_RATIO = 2 / 100;
const MARGIN_RATIO = 2 / 100;
const MAX_CARD_SIZE = 200;
const MIN_FONT_SIZE = 12;
const MAX_FONT_SIZE = 19;
const FONT_SIZE_RATIO = 2 / 100;
const BOUNDS_MARGIN = 90;
const CARD_MAX_SPACING_RATIO = 3 / 100;

const makeCardSpring = ({
    gestureState: {
        down,
        movement: [x, y],
    },
    left,
    top,
    immediate,
}: any) => ({
    x: down ? x : left,
    y: down ? y : top,
    z: down ? 10 : 0,
    immediate: (name) => (immediate || down) && ["x", "y"].includes(name),
    config: { mass: 1, tension: 170, friction: 25 },
});

interface CardProps {
    text: string;
    color: string;
    imageUrl: string;
    width: number;
    height: number;
    padding: number;
    left: number;
    top: number;
    onDrop(xy: number[]);
    bounds: { top: number; bottom: number; left: number; right: number };
    id: string;
    areaCode: any;
}

function CardSortCard({
    left,
    top,
    text,
    color,
    imageUrl,
    width,
    height,
    padding,
    bounds,
    onDrop,
    id,
    areaCode,
}: CardProps) {
    const recalledImage = useRecall(imageUrl);
    const recalledText = useRecall(text);
    const prevBounds = usePrevious(bounds);
    const theme = useTheme();
    const classes = useStyles({ theme, color });
    const drag = React.useRef<any>({ down: false, movement: [0, 0] });

    const [spring, set] = useSpring(() =>
        makeCardSpring({ gestureState: drag.current, left, top }),
    );

    const boundsChanged = prevBounds !== bounds;
    React.useLayoutEffect(() => {
        set(makeCardSpring({ gestureState: drag.current, left, top, immediate: boundsChanged }));
    }, [left, top, set, boundsChanged]);

    const bind = useDrag(
        (state) => {
            drag.current = state;
            set(makeCardSpring({ gestureState: state, left, top }));
            const { movement, down, tap } = state;
            if (tap) return;
            if (!down && onDrop) onDrop(movement);
        },
        {
            initial: () => [spring.x.getValue(), spring.y.getValue()],
            capture: true,
            filterTaps: true,
            bounds: {
                ...bounds,
                right: bounds.right - width,
                bottom: bounds.bottom - height,
            },
        },
    );

    return (
        <animated.div
            style={{
                width,
                height,
                transform: interpolate(
                    [spring.x, spring.y, spring.z],
                    (x, y, z) => `translate(${x}px, ${y}px) scale(${z * 0.01 + 1})`,
                ),
                boxShadow: spring.z.interpolate((z) => `0 ${z * 0.3}px ${z + 3}px rgba(0,0,0,0.2)`),
                zIndex: spring.z.interpolate((z) => Math.max(1, 1 + Math.round(z))),
                backgroundImage: recalledImage && `url('${recalledImage}')`,
                touchAction: "none",
            }}
            {...bind()}
            className={classes.card}
            id={id}
            data-areacode={areaCode}
        >
            <div
                style={{
                    padding,
                }}
                dangerouslySetInnerHTML={{ __html: recalledImage ? undefined : recalledText }}
            />
        </animated.div>
    );
}

interface BucketProps {
    text: string;
    color: string;
    width: number;
    height: number;
    lineHeight: number;
    left: number;
    top: number;
    id: string;
}

function CardSortBucket({ text, color, width, height, lineHeight, left, top, id }: BucketProps) {
    const theme = useTheme();
    const classes = useStyles({ theme, color });

    return (
        <div
            className={classes.bucket}
            style={{
                width,
                height,
                lineHeight: `${lineHeight}px`,
                left,
                top,
            }}
            dangerouslySetInnerHTML={{ __html: text }}
            id={id}
        />
    );
}

interface GroupProps {
    text: string;
    width: number;
    height: number;
    lineHeight: number;
    left: number;
    top: number;
}

function CardSortGroup({ text, width, height, lineHeight, left, top }: GroupProps) {
    const theme = useTheme();
    const classes = useStyles({ theme });

    return (
        <div
            className={classes.group}
            style={{
                width,
                height,
                lineHeight: `${lineHeight}px`,
                left,
                top,
            }}
            dangerouslySetInnerHTML={{ __html: text }}
        />
    );
}

export function CardSortControl(props: ControlProps) {
    const { question, sequencedVariables, validateSignal } = props;
    const [language] = useLanguage();
    const [insertionOrder, setInsertionOrder] = React.useState<number[]>([]);
    const theme = useTheme();
    const classes = useStyles({ theme });
    const containerRef = React.useRef<HTMLDivElement>();
    const { innerWidth } = useElementDimensions(containerRef);
    const choices = sequencedVariables[0].choiceList;
    const bucketPadding = innerWidth * PADDING_RATIO;
    const bucketMargin = innerWidth * MARGIN_RATIO;
    const cardSpacing = innerWidth * CARD_MAX_SPACING_RATIO;
    const fontSize = clamp(innerWidth * FONT_SIZE_RATIO, MIN_FONT_SIZE, MAX_FONT_SIZE);
    const lineHeight = fontSize * 3;
    const bucketWidth = innerWidth / 2 - bucketMargin / 2;
    const cardSize = clamp((bucketWidth - bucketPadding * 2) * CARD_SIZE_RATIO, 0, MAX_CARD_SIZE);
    const cardPadding = cardSize * PADDING_RATIO;
    const setMentions = useSetVariableMentions();
    const cardVariableMentions = usePickMentions(question.variableNames);
    const getCardGroup = React.useCallback(
        (card: VariableModel) => translate(card, "group", language),
        [language],
    );
    const mentionValueForCard = React.useCallback(
        (card: VariableModel) => {
            const mentions = cardVariableMentions[card.name] || [];
            return mentions[0] && mentions[0].value;
        },
        [cardVariableMentions],
    );
    const unselectedCards = React.useMemo(
        () => sequencedVariables.filter((c) => mentionValueForCard(c) === undefined),
        [sequencedVariables, mentionValueForCard],
    );
    const groups = React.useMemo(() => uniq(sequencedVariables.map(getCardGroup)), [
        sequencedVariables,
        getCardGroup,
    ]);
    const bucketHeight = lineHeight + cardSize + bucketPadding;
    const bucketsHeight = Math.ceil(choices.length / 2) * (bucketHeight + bucketMargin);
    const groupsHeight = Math.max(Math.ceil(groups.length / 2), 1) * (bucketHeight + bucketMargin);
    const containerHeight = bucketsHeight + groupsHeight;

    const bounds = React.useMemo(
        () => ({
            top: -BOUNDS_MARGIN,
            bottom: containerHeight + BOUNDS_MARGIN,
            left: -BOUNDS_MARGIN,
            right: innerWidth + BOUNDS_MARGIN,
        }),
        [innerWidth, containerHeight],
    );

    useValidation(validateSignal, useDiscreteVariadicValidator(question, sequencedVariables));

    const insertionOrderSort = (a, b) =>
        insertionOrder.indexOf(a.id) - insertionOrder.indexOf(b.id);

    const unselectedCardGroup = (group: string) =>
        unselectedCards.filter((v) => getCardGroup(v) === group).sort(insertionOrderSort);

    const cardsForChoice = (choice: ChoiceModel) =>
        sequencedVariables
            .filter((v) => mentionValueForCard(v) === choice.value)
            .sort(insertionOrderSort);

    const cardTop = (card: VariableModel) => {
        const value = mentionValueForCard(card);
        const group = getCardGroup(card);
        const i = choices.findIndex((c) => c.value === value);
        if (i === -1) {
            const groupIndex = groups.indexOf(group);
            return (
                bucketsHeight +
                Math.floor(groupIndex / 2) * (bucketHeight + bucketMargin) +
                lineHeight
            );
        }
        return Math.floor(i / 2) * (bucketHeight + bucketMargin) + lineHeight;
    };

    const cardLeft = (card: VariableModel) => {
        const value = mentionValueForCard(card);
        const choice = choices.find((c) => c.value === value);
        const group = getCardGroup(card);
        const cards = choice ? cardsForChoice(choice) : unselectedCardGroup(group);
        const spacing = clamp(
            (bucketWidth - bucketPadding - cardSize) / cards.length,
            0,
            cardSpacing,
        );
        const choiceIndex = choice ? choices.indexOf(choice) : groups.indexOf(group);
        const cardIndex = cards.indexOf(card);
        const offset = cardIndex * spacing;
        return (choiceIndex % 2) * (innerWidth / 2 + bucketMargin / 2) + offset + bucketPadding;
    };

    const onChoiceDrop = (card: VariableModel) => ([x, y]: number[]) => {
        const cx = x + cardSize / 2;
        const cy = y + cardSize / 2;
        const r = Math.floor(cy / bucketHeight);
        const c = cx < innerWidth / 2 ? 0 : 1;
        const i = c + r * 2;
        const dropBucket = i >= 0 && i < choices.length ? i : undefined;

        if (dropBucket !== undefined) {
            const dropChoice = choices[dropBucket];
            setMentions(card.name, (b) => b.set(dropChoice.value));
        } else {
            setMentions(card.name, (b) => b.clear());
        }
        setInsertionOrder([...insertionOrder.filter((id) => id !== card.id), card.id]);
    };

    // To Do : remove duplicated code from BaseControl.tsx
    const getMentions = (props: ControlProps, variableName?: string) => {
        const [variable] = props.question.variables;
        const mentions = props.mentions[(variableName || variable.name).toUpperCase()] || [];
        return mentions.filter((mention) => mention.matrix === props.matrix);
    };

    // To Do : remove duplicated code from BaseControl.tsx
    const getMention = (props: ControlProps, variableName?: string, mentionIndex?: number) =>
        getMentions(props, variableName).find((m) => m.index === (mentionIndex || 1));

    // To Do : remove duplicated code from BaseControl.tsx
    const getMentionValue = (props: ControlProps, variableName?: string, mentionIndex?: number) =>
        (getMention(props, variableName, mentionIndex) || { value: "" }).value;

    const getCurrentChoice = (props: ControlProps, variableName: VariableModel) => {
        const value = getMentionValue(props, variableName.name);
        return getChoice(variableName, value);
    };

    return (
        <div
            className={classes.container}
            ref={containerRef}
            style={{
                height: containerHeight,
                fontSize: `${fontSize}px`,
            }}
        >
            {sequencedVariables[0].choiceList.map((c, i) => (
                <CardSortBucket
                    id={c.elementId}
                    key={c.name}
                    color={getSettingString(c, "Color")}
                    text={translate(c, "text", language)}
                    top={Math.floor(i / 2) * (bucketHeight + bucketMargin)}
                    left={(i % 2) * (innerWidth / 2 + bucketMargin / 2)}
                    width={bucketWidth}
                    height={bucketHeight}
                    lineHeight={lineHeight}
                />
            ))}
            {groups.map((g, i) => (
                <CardSortGroup
                    key={g}
                    text={g}
                    top={bucketsHeight + Math.floor(i / 2) * (bucketHeight + bucketMargin)}
                    left={(i % 2) * (innerWidth / 2 + bucketMargin / 2)}
                    width={bucketWidth}
                    height={bucketHeight}
                    lineHeight={lineHeight}
                />
            ))}
            {sequencedVariables.sort(insertionOrderSort).map((v) => {
                const choice = getCurrentChoice(props, v);

                return (
                    <CardSortCard
                        id={v.elementId}
                        key={v.name}
                        color={getSettingString(v, "Color")}
                        text={translate(v, "text", language)}
                        imageUrl={translate(v, "imageUrl", language)}
                        onDrop={onChoiceDrop(v)}
                        top={cardTop(v)}
                        left={cardLeft(v)}
                        width={cardSize}
                        height={cardSize}
                        padding={cardPadding}
                        bounds={bounds}
                        areaCode={choice !== undefined ? choice.value : "none"}
                    />
                );
            })}
        </div>
    );
}
